// import { PROMPT_SECUREID } from '@comeon/actions-public';
import type { EnhancedImage } from '~/components/utilities/image-format';
import { HttpMethods, requestApi } from '../../../services/api-new';
import { AppDispatch, Item, Response, ThunkAction } from '../types';
import {
	BONUS_ALLOWED_BY_PAYMENT,
	FETCH_BONUS_FAILURE,
	FETCH_BONUS_START,
	FETCH_BONUS_SUCCESS,
	USING_HYDRATED_BONUSES
} from './constants';
import { BonusesState } from './selectors';

export type ResponseItem = Omit<Item, 'imageView' | 'imageViewVO'> & {
	imageView?: EnhancedImage;
	imageViewVO?: EnhancedImage;
};

export async function wrappedRequestApi({
	endpoint,
	payload,
	method,
	urlencodedContentType,
	netellerHeaders
}: {
	endpoint: string;
	payload?: object;
	method?: keyof typeof HttpMethods;
	urlencodedContentType?: boolean;
	netellerHeaders?: boolean;
}) {
	const response: Response = await requestApi({
		endpoint,
		payload,
		method,
		urlencodedContentType,
		netellerHeaders
	});
	return response.result && typeof response.result === 'string' && response.status !== 'FAILURE'
		? (JSON.parse(response.result) as ResponseItem[] | ResponseItem)
		: typeof response.result === 'object' && response.status !== 'FAILURE'
		? (response.result as ResponseItem[] | ResponseItem)
		: {
				error: {
					errorCode: response.errorCode,
					errorDescription: response.errorDescription,
					status: response.status
				}
		  };
}

export function allowsMethod(mId?: number, idStr?: string) {
	// when no rules are specified the method is allowed
	const emptyIdString = !idStr || !(idStr.length && idStr.length > 0);
	const noMethod = !mId;
	if (emptyIdString || idStr === 'ALL' || noMethod) return true;
	const hasIdInString = !!idStr.split(',').find(value => parseInt(value, 10) === mId);
	return hasIdInString;
}

export function isAllowedForMethod(paymentMethodId: number) {
	// the ducks/payment.js use this to update for a new selected paymentmethod.
	return (dispatch: AppDispatch, getState: () => BonusesState) => {
		const { paymentMethodIdsStr } = getState().bonuses;
		const allowedForMethod = allowsMethod(paymentMethodId, paymentMethodIdsStr);
		return dispatch({
			type: BONUS_ALLOWED_BY_PAYMENT,
			payload: { allowedForMethod }
		});
	};
}

// could be in a shared common.js file
export function sortById(arr: object[]) {
	let obj: { [key: number]: object } | null = null;
	let key: number;
	if (arr && arr.length > 0) {
		obj = {};
		arr.forEach(elm => {
			if ('id' in elm && obj) {
				key = elm.id as number;
				obj[key] = elm;
			}
		});
	}
	return obj;
}

export function getBonus() {
	return async (dispatch: AppDispatch, getState: () => BonusesState) => {
		const {
			bonuses: { hydrated = false }
		} = getState();
		if (hydrated) {
			return dispatch({ type: USING_HYDRATED_BONUSES });
		}
		dispatch({ type: FETCH_BONUS_START });

		const timestamp = Date.now();
		const result = await wrappedRequestApi({ endpoint: `/rest/bonus/depositoffers?d=${timestamp}` });
		if (!('error' in result)) {
			const onId = sortById('length' in result ? result : [result]);
			return dispatch({
				type: FETCH_BONUS_SUCCESS,
				payload: { result, onId }
			});
		}

		return dispatch({
			type: FETCH_BONUS_FAILURE
		});
	};
}

export function getRegistrationBonus(errorActionCreator: () => ThunkAction) {
	return (
		/**
		 * @param {object} dispatch
		 */
		async (dispatch: AppDispatch) => {
			dispatch({ type: FETCH_BONUS_START });

			try {
				const result = await wrappedRequestApi({ endpoint: '/rest/bonus/availablebonusesonregistration' });
				if (result) {
					if (!('error' in result)) {
						const onId = sortById('length' in result ? result : [result]);
						return dispatch({
							type: FETCH_BONUS_SUCCESS,
							payload: { result, onId }
						});
					}
				}
				return dispatch({
					type: FETCH_BONUS_FAILURE
				});
			} catch (e) {
				// eslint-disable-next-line no-console
				console.warn('Error while fetching bonus: ', e);
				/* catch a server error */
				if (errorActionCreator) {
					dispatch(errorActionCreator());
				}
				return dispatch({ type: FETCH_BONUS_FAILURE });
			}
		}
	);
}

export const getIsFreebetClaimedInBonusPackage = async (freebetId: number) =>
	requestApi({ endpoint: `/rest/welcomebonuspackages/${freebetId}/is-claimed` });
